import { EventType } from 'matrix-js-sdk/src/matrix';
import { useMemo } from 'react';

import { CustomMeedioMatrixEvents, RoomPowerLevelOverridesOptions } from 'utils/matrixUtils';
import { getScopedRoomActionPowerLevels, PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';

export type RoomCreationScope = 'chat' | 'meeting';

/** Creator PL at room creation — must be >= join_rules event threshold; bot demotes to 90 (or higher) after validation. */
export const ROOM_CREATOR_PL_AT_CREATION = 99;

export const V2_ROOM_MANAGERS_EVENT_THRESHOLD = workspacesConfig[PermissionId.MeetingReassignManager].powerLevel;

/** Meeting rooms: team/shared/knock via access event — aligned with MeetingToggleWaitingList (PL 25). */
export const V2_MEETING_ROOM_ACCESS_EVENT_THRESHOLD =
  workspacesConfig[PermissionId.MeetingToggleWaitingList].powerLevel;

/** Chat rooms: team/shared — aligned with WorkspaceInviteMembers (PL 30); bot still validates workspace PL on shared. */
export const V2_CHAT_ROOM_ACCESS_EVENT_THRESHOLD = workspacesConfig[PermissionId.WorkspaceInviteMembers].powerLevel;

export const getV2RoomAccessEventThreshold = (scope: RoomCreationScope): number =>
  scope === 'meeting' ? V2_MEETING_ROOM_ACCESS_EVENT_THRESHOLD : V2_CHAT_ROOM_ACCESS_EVENT_THRESHOLD;

export const V2_ROOM_CREATION_CREATION_EVENT_THRESHOLDS = {
  'eu.meedio.room.managers': V2_ROOM_MANAGERS_EVENT_THRESHOLD,
  'eu.meedio.room.access': V2_MEETING_ROOM_ACCESS_EVENT_THRESHOLD,
} as const;

export const V2_ROOM_JOIN_RULES_STEADY_STATE_THRESHOLD = 99;

export const buildV2RoomCreationEventThresholds = (scope: RoomCreationScope) =>
  ({
    'eu.meedio.room.managers': V2_ROOM_MANAGERS_EVENT_THRESHOLD,
    'eu.meedio.room.access': getV2RoomAccessEventThreshold(scope),
    'm.room.join_rules': V2_ROOM_JOIN_RULES_STEADY_STATE_THRESHOLD,
  }) as const;

/** @deprecated Prefer `buildV2RoomCreationEventThresholds('meeting')` — meeting-scoped defaults. */
export const V2_ROOM_CREATION_EVENT_THRESHOLDS = buildV2RoomCreationEventThresholds('meeting');

/** Messenger chat room events gated by chat-scoped call permissions (BUG-016). */
export const V2_CHAT_MESSENGER_EVENT_THRESHOLDS = {
  [CustomMeedioMatrixEvents.MESSENGER_CALL]: workspacesConfig[PermissionId.ChatCreateInstantCall].powerLevel,
  [CustomMeedioMatrixEvents.MEETING_STARTED]: workspacesConfig[PermissionId.ChatCreateInstantCall].powerLevel,
  [CustomMeedioMatrixEvents.SCHEDULED_MEETING]: workspacesConfig[PermissionId.ChatScheduleMeeting].powerLevel,
} as const;

/** Chat metadata state events gated by Manage chat settings (MIP-035 PL 30; BUG-017 Fix C). */
export const V2_CHAT_METADATA_EVENT_THRESHOLDS = {
  [EventType.RoomName]: workspacesConfig[PermissionId.ChatManageSettings].powerLevel,
  [EventType.RoomTopic]: workspacesConfig[PermissionId.ChatManageSettings].powerLevel,
  [EventType.RoomAvatar]: workspacesConfig[PermissionId.ChatManageSettings].powerLevel,
} as const;

export interface RoomCreationPowerLevelsConfig {
  powerLevelOverrides: RoomPowerLevelOverridesOptions;
  ownerPowerLevel: number;
}

export const mergeRoomCreationPowerLevelOverrides = (
  workspaceOverrides?: RoomPowerLevelOverridesOptions
): RoomPowerLevelOverridesOptions => {
  const scopedActions = getScopedRoomActionPowerLevels('meeting');
  const { redact: _workspaceRedact, ...workspaceWithoutRedact } = workspaceOverrides ?? {};

  return {
    ...workspaceWithoutRedact,
    ...scopedActions,
    events: {
      ...workspaceOverrides?.events,
      ...V2_ROOM_CREATION_CREATION_EVENT_THRESHOLDS,
      'm.room.join_rules': V2_ROOM_JOIN_RULES_STEADY_STATE_THRESHOLD,
    },
  };
};

const useRoomCreationPowerLevels = (scope: RoomCreationScope): RoomCreationPowerLevelsConfig =>
  useMemo(
    () => ({
      powerLevelOverrides: {
        ...getScopedRoomActionPowerLevels(scope),
        events: {
          ...buildV2RoomCreationEventThresholds(scope),
          ...(scope === 'chat' ? { ...V2_CHAT_MESSENGER_EVENT_THRESHOLDS, ...V2_CHAT_METADATA_EVENT_THRESHOLDS } : {}),
        },
      },
      ownerPowerLevel: ROOM_CREATOR_PL_AT_CREATION,
    }),
    [scope]
  );

export default useRoomCreationPowerLevels;
